import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Descriptions, Drawer, Form, Input, InputNumber, message, Modal, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import { ArrowLeftOutlined, DownloadOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { copyToClipboard } from '../../helpers/clipboard';
import { useUserInfo } from '../../contexts/UserInfoContext';
import { useSalePolicy } from '../../contexts/SalePolicyContext';
import { downloadSaleBatch, formatSaleCode, newSaleRequestId, SaleBatch, SaleCode, SaleEvent, SaleLink, SalePage, saleRequest, saleShareUrl } from '../../services/saleLinks';
import { saleDate, saleError, saleStatus, useSaleText } from './saleUi';
import './saleLinks.css';

interface BatchInput { name: string; quantity: number; remark?: string }
const emptyCodes: SalePage<SaleCode> = { data: [], page: 1, pageSize: 20, total: 0 };

const SaleCodes: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const text = useSaleText();
  const { isAuthenticated } = useUserInfo();
  const [link, setLink] = useState<SaleLink | null>(null);
  const { policy, error: policyError } = useSalePolicy();
  const [codes, setCodes] = useState(emptyCodes);
  const [batches, setBatches] = useState<SaleBatch[]>([]);
  const [batchPage, setBatchPage] = useState(1);
  const [batchTotal, setBatchTotal] = useState(0);
  const [batchId, setBatchId] = useState<number | undefined>();
  const [status, setStatus] = useState('');
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generateOpen, setGenerateOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [form] = Form.useForm<BatchInput>();
  const submitted = useRef<(BatchInput & { requestId: string }) | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selected, setSelected] = useState<SaleCode | null>(null);
  const [events, setEvents] = useState<SalePage<SaleEvent>>({ data: [], total: 0, page: 1, pageSize: 20 });
  const [eventPage, setEventPage] = useState(1);
  const [detailLoading, setDetailLoading] = useState(false);
  const [note, setNote] = useState('');
  const [acting, setActing] = useState(false);
  const [replacementOpen, setReplacementOpen] = useState(false);
  const [replacementReason, setReplacementReason] = useState('');
  const [replacement, setReplacement] = useState<SaleCode | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !/^\d+$/.test(id)) return;
    let active = true;
    setLoading(true); setError('');
    const params = new URLSearchParams({ page: String(page), pageSize: '20', status, keyword });
    if (batchId) params.set('batchId', String(batchId));
    void Promise.all([
      saleRequest<SaleLink>(`/${id}`),
      saleRequest<SalePage<SaleCode>>(`/${id}/codes?${params}`),
    ]).then(([nextLink, result]) => {
      if (active) { setLink(nextLink); setCodes(result); }
    }).catch((failure) => { if (active) setError(saleError(failure, text)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [isAuthenticated, id, page, batchId, status, keyword, revision, text]);

  useEffect(() => {
    if (!isAuthenticated || !/^\d+$/.test(id)) return;
    let active = true;
    void saleRequest<SalePage<SaleBatch>>(`/${id}/batches?page=${batchPage}&pageSize=100`).then((result) => {
      if (!active) return;
      setBatchTotal(result.total);
      setBatches((previous) => batchPage === 1 ? result.data : [...previous.filter((batch) => !result.data.some((item) => item.id === batch.id)), ...result.data]);
    }).catch((failure) => { if (active) setError(saleError(failure, text)); });
    return () => { active = false; };
  }, [isAuthenticated, id, batchPage, revision, text]);

  const reload = useCallback(() => setRevision((value) => value + 1), []);
  useEffect(() => {
    if (!selectedId) { setSelected(null); return; }
    let active = true;
    setDetailLoading(true);
    void Promise.all([
      saleRequest<SaleCode>(`/${id}/codes/${selectedId}`),
      saleRequest<SalePage<SaleEvent>>(`/${id}/codes/${selectedId}/events?page=${eventPage}`),
    ]).then(([code, history]) => {
      if (active) { setSelected(code); setNote(code.remark || ''); setEvents(history); }
    }).catch((failure) => { if (active) message.error(saleError(failure, text)); })
      .finally(() => { if (active) setDetailLoading(false); });
    return () => { active = false; };
  }, [id, selectedId, eventPage, revision, text]);

  const generate = async () => {
    if (generating) return;
    let values: BatchInput;
    try { values = await form.validateFields(); } catch { return; }
    setGenerating(true);
    try {
      if (!submitted.current) submitted.current = { ...values, requestId: newSaleRequestId() };
      const batch = await saleRequest<SaleBatch>(`/${id}/batches`, 'POST', submitted.current);
      setGenerateOpen(false); setBatchId(batch.id); setPage(1); setBatchPage(1); reload();
      message.success(text('激活码已生成，可复制或导出交付。', 'Codes generated. Copy or export them for delivery.'));
    } catch (failure) { message.error(saleError(failure, text)); }
    finally { setGenerating(false); }
  };

  const deliveryText = (code: SaleCode): string => `${link?.title}\n${link ? saleShareUrl(link.linkCode, policy?.saleOrigin) : ''}\n${text('激活码：', 'Activation code: ')}${formatSaleCode(code.codeValue)}\n\n${text('首次激活时选择绑定设备，或在账号功能开放时绑定邮箱账号。一个码只能绑定一次，绑定方式不可更改；账号绑定可跨设备登录使用。', 'Choose device binding or, when available, email account binding on first activation. Each code binds once and cannot change mode. Account binding works across devices.')}`;
  const copyCode = (code: SaleCode) => copyToClipboard(formatSaleCode(code.codeValue), text('激活码已复制', 'Code copied'));

  const editCode = async (code: SaleCode, values: { remark?: string; delivered?: boolean }) => {
    if (acting) return;
    setActing(true);
    try { await saleRequest(`/${id}/codes/${code.id}`, 'PATCH', values); reload(); }
    catch (failure) { message.error(saleError(failure, text)); }
    finally { setActing(false); }
  };

  const disable = (code: SaleCode) => Modal.confirm({
    title: text('禁用此激活码？', 'Disable this code?'),
    content: text('该码及对应设备或账号授权将无法再次通过售卖入口访问。', 'This code and its device or account authorization will no longer grant entry.'),
    okText: text('禁用', 'Disable'), cancelText: text('取消', 'Cancel'), okButtonProps: { danger: true },
    onOk: async () => {
      try { await saleRequest(`/${id}/codes/${code.id}/disable`, 'POST'); reload(); }
      catch (failure) { message.error(saleError(failure, text)); throw failure; }
    },
  });

  const reissue = async () => {
    if (!selected || acting || !replacementReason.trim()) return;
    setActing(true);
    try {
      const next = await saleRequest<SaleCode>(`/${id}/codes/${selected.id}/reissue`, 'POST', { reason: replacementReason });
      setReplacement(next); setReplacementOpen(false); setBatchPage(1); reload();
    } catch (failure) { message.error(saleError(failure, text)); }
    finally { setActing(false); }
  };

  return <div className="sale-page">
    <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate('/sale-links')}>{text('售卖链接', 'Sale links')}</Button>
    <Typography.Title level={2}>{link?.title || text('激活码管理', 'Activation codes')}</Typography.Title>
    {!isAuthenticated && <Alert type="info" message={text('请先登录后查看激活码。', 'Sign in to view activation codes.')} />}
    {(error || policyError) && <Alert type="error" showIcon message={error || policyError?.message} />}
    {link && <>
      <Space wrap><Tag color="blue">{saleStatus(link.status, text)}</Tag><Typography.Text copyable>{saleShareUrl(link.linkCode, policy?.saleOrigin)}</Typography.Text></Space>
      <Row gutter={16} className="sale-stats">
        <Col span={8}><Statistic title={text('总生成码数', 'Generated codes')} value={link.codeCount || 0} /></Col>
        <Col span={8}><Statistic title={text('曾激活码数', 'Ever activated')} value={link.activatedCount || 0} /></Col>
        <Col span={8}><Statistic title={text('打开次数', 'Openings')} value={link.accessCount || 0} /></Col>
      </Row>
    </>}
    <Space wrap className="sale-toolbar">
      <Button type="primary" aria-label={text('批量生成', 'Generate codes')} icon={<PlusOutlined />} disabled={!policy?.creationEnabled || link?.status !== 'ACTIVE'} onClick={() => {
        submitted.current = null; form.resetFields(); form.setFieldsValue({ quantity: Math.min(100, policy?.maxBatchSize || 100), name: new Date().toLocaleDateString() }); setGenerateOpen(true);
      }}>{text('批量生成', 'Generate codes')}</Button>
      <Select allowClear showSearch optionFilterProp="label" placeholder={text('全部批次', 'All batches')} value={batchId} style={{ minWidth: 190 }}
        onChange={(value) => { setBatchId(value); setPage(1); }} options={batches.map((batch) => ({ value: batch.id, label: `${batch.name} (#${batch.id})` }))} />
      {batches.length < batchTotal && <Button onClick={() => setBatchPage((value) => value + 1)}>{text('加载更多批次', 'More batches')}</Button>}
      <Select value={status} style={{ minWidth: 130 }} onChange={(value) => { setStatus(value); setPage(1); }} options={[
        { value: '', label: text('全部状态', 'All statuses') }, ...['UNUSED', 'ACTIVATED', 'DISABLED', 'REPLACED'].map((value) => ({ value, label: saleStatus(value, text) })),
      ]} />
      <Input.Search allowClear placeholder={text('搜索完整激活码或备注', 'Search full code or note')} onSearch={(value) => { setKeyword(value); setPage(1); }} />
      <Button icon={<DownloadOutlined />} disabled={!batchId} loading={exporting} onClick={async () => {
        if (!batchId) return; setExporting(true);
        try { await downloadSaleBatch(Number(id), batchId); } catch (failure) { message.error(saleError(failure, text)); } finally { setExporting(false); }
      }}>{text('导出所选批次', 'Export selected batch')}</Button>
      <Button icon={<ReloadOutlined />} onClick={reload}>{text('刷新', 'Refresh')}</Button>
    </Space>
    <Card>
      <Table<SaleCode> rowKey="id" dataSource={codes.data} loading={loading} scroll={{ x: 1250 }}
        pagination={{ current: page, total: codes.total, pageSize: 20, showSizeChanger: false, onChange: setPage }} columns={[
          { title: text('激活码', 'Code'), dataIndex: 'codeValue', width: 285, render: (value, code) => <Button type="link" className="sale-code-value"
            onClick={() => { setSelected(null); setEvents({ data: [], total: 0, page: 1, pageSize: 20 }); setEventPage(1); setSelectedId(code.id); }}>{formatSaleCode(value)}</Button> },
          { title: text('批次', 'Batch'), dataIndex: 'batchId', render: (value) => batches.find((batch) => batch.id === value)?.name || `#${value}` },
          { title: text('状态', 'Status'), dataIndex: 'status', render: (value) => <Tag color={value === 'ACTIVATED' ? 'blue' : 'default'}>{saleStatus(value, text)}</Tag> },
          { title: text('绑定方式', 'Binding'), dataIndex: 'bindingMode', render: (value) => value === 'ACCOUNT' ? text('邮箱账号', 'Email account') : value === 'DEVICE' ? text('设备', 'Device') : text('待选择', 'Not bound') },
          { title: text('买家邮箱', 'Buyer email'), dataIndex: 'buyerEmail', render: (value) => value || '-' },
          { title: text('发放', 'Delivery'), dataIndex: 'deliveredAt', render: (value, code) => <Button type="link" disabled={acting} onClick={() => void editCode(code, { delivered: !value })}>{value ? text('已发放', 'Delivered') : text('未标记', 'Unmarked')}</Button> },
          { title: text('激活时间', 'Activated'), dataIndex: 'activatedAt', render: saleDate },
          { title: text('最近打开', 'Last opened'), dataIndex: 'lastAccessTime', render: saleDate },
          { title: text('打开次数', 'Openings'), dataIndex: 'accessCount' },
          { title: text('备注', 'Note'), dataIndex: 'remark', ellipsis: true },
          { title: text('操作', 'Actions'), fixed: 'right', width: 150, render: (_, code) => <Space>
            <Button type="link" aria-label={text('复制激活码', 'Copy activation code')} onClick={() => copyCode(code)}>{text('复制码', 'Copy')}</Button>
            <Button type="link" onClick={() => copyToClipboard(deliveryText(code), text('交付文本已复制', 'Delivery text copied'))}>{text('交付', 'Deliver')}</Button>
          </Space> },
        ]} />
      <Typography.Paragraph type="secondary">{text('点击激活码查看详情。复制或导出不会自动标记已发放；激活数不等于销量。', 'Click a code for details. Copying or exporting does not mark it delivered; activations are not sales.')}</Typography.Paragraph>
    </Card>

    <Modal title={text('批量生成激活码', 'Generate activation codes')} open={generateOpen} onCancel={() => !generating && setGenerateOpen(false)}
      onOk={() => void generate()} confirmLoading={generating} okText={text('生成 / 重试', 'Generate / retry')}
      okButtonProps={{ 'aria-label': text('生成 / 重试', 'Generate / retry') }} cancelText={text('取消', 'Cancel')}>
      <Form form={form} layout="vertical" disabled={!!submitted.current}>
        <Form.Item name="name" label={text('批次名称', 'Batch name')} rules={[{ required: true, whitespace: true }]}><Input maxLength={100} /></Form.Item>
        <Form.Item name="quantity" label={text('数量', 'Quantity')} rules={[{ required: true }]}><InputNumber min={1} max={policy?.maxBatchSize || 1000} precision={0} /></Form.Item>
        <Form.Item name="remark" label={text('批次备注', 'Batch note')}><Input.TextArea maxLength={500} /></Form.Item>
      </Form>
      <Alert type="info" message={text('每个码由买家首次激活时选择设备或邮箱账号绑定，不设到期时间；账号绑定需要开通买家账号功能。', 'Buyers choose device or email account binding on first use. No expiry; account binding requires the buyer account feature.')} />
    </Modal>

    <Drawer open={selectedId !== null} onClose={() => setSelectedId(null)} width={Math.min(680, window.innerWidth)} title={text('激活码详情', 'Code details')}>
      <Card loading={detailLoading} bordered={false} className="sale-detail">
        {selected && <>
          <Typography.Paragraph className="sale-code-value" copyable={{ text: formatSaleCode(selected.codeValue) }}>{formatSaleCode(selected.codeValue)}</Typography.Paragraph>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={text('状态', 'Status')}>{saleStatus(selected.status, text)}</Descriptions.Item>
            <Descriptions.Item label={text('批次', 'Batch')}>{selected.batchName}</Descriptions.Item>
            <Descriptions.Item label={text('绑定方式', 'Binding')}>{selected.bindingMode === 'ACCOUNT' ? text('邮箱账号', 'Email account') : selected.bindingMode === 'DEVICE' ? text('设备', 'Device') : text('待选择', 'Not bound')}</Descriptions.Item>
            <Descriptions.Item label={text('买家邮箱', 'Buyer email')}>{selected.buyerEmail || '-'}</Descriptions.Item>
            <Descriptions.Item label={text('浏览器', 'Browser')}>{selected.deviceLabel || '-'}</Descriptions.Item>
            <Descriptions.Item label={text('生成时间', 'Created')}>{saleDate(selected.createTime)}</Descriptions.Item>
            <Descriptions.Item label={text('激活时间', 'Activated')}>{saleDate(selected.activatedAt)}</Descriptions.Item>
            <Descriptions.Item label={text('最近打开', 'Last opened')}>{saleDate(selected.lastAccessTime)}</Descriptions.Item>
            <Descriptions.Item label={text('打开次数', 'Openings')}>{selected.accessCount}</Descriptions.Item>
            <Descriptions.Item label={text('授权期限', 'Authorization expiry')}>{text('不设到期时间，受资源状态限制', 'No expiry; resource availability applies')}</Descriptions.Item>
          </Descriptions>
          <Space direction="vertical" style={{ width: '100%', marginTop: 16 }}>
            <Input.TextArea aria-label={text('激活码备注', 'Code note')} value={note} maxLength={500} onChange={(event) => setNote(event.target.value)} />
            <Button loading={acting} onClick={() => void editCode(selected, { remark: note })}>{text('保存备注', 'Save note')}</Button>
          </Space>
          <Space wrap className="sale-toolbar">
            <Button danger disabled={!['UNUSED', 'ACTIVATED'].includes(selected.status)} onClick={() => disable(selected)}>{text('禁用', 'Disable')}</Button>
            <Button disabled={!['ACTIVATED', 'DISABLED'].includes(selected.status) || link?.status === 'REVOKED'} onClick={() => { setReplacementReason(''); setReplacementOpen(true); }}>{text('补发新码', 'Replace code')}</Button>
            {selected.replacementId && <Button onClick={() => { setSelectedId(selected.replacementId); setEventPage(1); }}>{text('查看补发的新码', 'View replacement')}</Button>}
          </Space>
          <Typography.Title level={5}>{text('访问和操作记录', 'Usage and support history')}</Typography.Title>
          <Table<SaleEvent> size="small" rowKey="id" dataSource={events.data} scroll={{ x: 450 }} pagination={{ current: eventPage, total: events.total, pageSize: 20, showSizeChanger: false, onChange: setEventPage }} columns={[
            { title: text('时间', 'Time'), dataIndex: 'createTime', render: saleDate },
            { title: text('事件', 'Event'), dataIndex: 'eventType', render: (value: string) => ({
              OPEN: text('打开资源', 'Opened'), ACTIVATED: text('首次激活', 'Activated'), DISABLED: text('禁用', 'Disabled'),
              REPLACED: text('补发', 'Replaced'), REPLACEMENT_CREATED: text('补发新码', 'Replacement created'), CODE_EDITED: text('备注 / 发放更新', 'Delivery / note updated'),
            }[value] || value) },
            { title: text('详情', 'Details'), render: (_, event) => <span>{event.detail} {event.ipHint}</span> },
          ]} />
          <Typography.Text type="secondary">{text('访问明细保留 90 天，激活和售后历史持续保留。', 'Opening details are retained for 90 days; activation and support history are preserved.')}</Typography.Text>
        </>}
      </Card>
    </Drawer>
    <Modal open={replacementOpen} title={text('补发激活码', 'Replace activation code')} onCancel={() => setReplacementOpen(false)} onOk={() => void reissue()}
      confirmLoading={acting} okButtonProps={{ disabled: !replacementReason.trim() }} okText={text('作废旧码并补发', 'Revoke old code and replace')} cancelText={text('取消', 'Cancel')}>
      <Alert type="warning" message={text('旧设备或账号授权将失效，新码需要重新选择绑定方式并激活。', 'The old device or account loses authorization. Choose a binding mode and activate the new code.')} />
      <Input.TextArea style={{ marginTop: 16 }} placeholder={text('填写补发原因，例如更换设备', 'Reason, e.g. changed device')} maxLength={500} value={replacementReason} onChange={(event) => setReplacementReason(event.target.value)} />
    </Modal>
    <Modal open={!!replacement} title={text('已补发新码', 'Replacement ready')} onCancel={() => setReplacement(null)} footer={null}>
      {replacement && <Space direction="vertical"><Typography.Paragraph copyable={{ text: formatSaleCode(replacement.codeValue) }}>{formatSaleCode(replacement.codeValue)}</Typography.Paragraph>
        <Button type="primary" onClick={() => copyToClipboard(deliveryText(replacement), text('交付文本已复制', 'Delivery text copied'))}>{text('复制交付文本', 'Copy delivery text')}</Button></Space>}
    </Modal>
  </div>;
};
export default SaleCodes;
